import { useCallback, useEffect, useRef } from "react";

import { useTypedDispatch, useTypedSelector } from "../redux/store";
import connectionQuality from "../redux/reducers/connectionQuality";
import {
	CLOUDFLARE_TRACE_URL,
	EXTERNAL_CHECK_MIN_GAP_MS,
	EXTERNAL_TIMEOUT_MS,
	GOOGLE_CHECK_URL,
	PING_INTERVAL_MS,
	PING_TIMEOUT_MS,
	PingSample,
	getConnectionStats,
	getInternetVerdict,
	needsExternalCheck,
	parseCloudflareTraceIp,
} from "../utils/connectionQuality";

import { getPRPC } from "./usePRPC";

const TIMEOUT = Symbol("timeout");

const pingServer = async (): Promise<PingSample> => {
	const ping = getPRPC()?.theirsModel?.utils?.ping;
	if (typeof ping !== "function") return null;

	let timer: ReturnType<typeof setTimeout> | undefined;
	const start = performance.now();
	try {
		const response = await Promise.race([
			ping(),
			new Promise<typeof TIMEOUT>((resolve) => {
				timer = setTimeout(() => resolve(TIMEOUT), PING_TIMEOUT_MS);
			}),
		]);
		if (response === TIMEOUT || response?.status !== 200) return null;
		return Math.round(performance.now() - start);
	} catch {
		return null;
	} finally {
		clearTimeout(timer);
	}
};

const fetchExternal = async (url: string, init?: RequestInit) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), EXTERNAL_TIMEOUT_MS);
	try {
		// Unique query + no-store so the browser cache cannot answer for the site.
		return await fetch(`${url}?t=${Date.now()}`, {
			...init,
			cache: "no-store",
			signal: controller.signal,
		});
	} finally {
		clearTimeout(timer);
	}
};

/** Google sends no CORS header: only no-cors tells "answered" from "no network". */
const checkGoogle = async (): Promise<boolean> => {
	try {
		await fetchExternal(GOOGLE_CHECK_URL, { mode: "no-cors" });
		return true;
	} catch {
		return false;
	}
};

const readCloudflare = async (): Promise<{ ok: boolean; ip?: string }> => {
	try {
		const response = await fetchExternal(CLOUDFLARE_TRACE_URL);
		if (!response.ok) return { ok: false };
		return { ok: true, ip: parseCloudflareTraceIp(await response.text()) };
	} catch {
		return { ok: false };
	}
};

/**
 * Measures the link to our server for the header and login indicators.
 * Mounted once in App: the header is recreated on every page. Runs before
 * login too: `utils.ping` needs no session, and a failed login is where
 * the dispatcher most needs to know whose link is down.
 */
const useConnectionQualityMonitor = () => {
	const dispatch = useTypedDispatch();
	const connected = useTypedSelector((state) => state.prpc.connected);
	const { online, samples, lastCheckAt } = useTypedSelector(
		(state) => state.connectionQuality,
	);

	const refreshExternalIp = useCallback(async () => {
		const { ip } = await readCloudflare();
		if (ip) dispatch(connectionQuality.actions.setExternalIp(ip));
	}, [dispatch]);

	useEffect(() => {
		const onOnline = () => {
			dispatch(connectionQuality.actions.setOnline(true));
			refreshExternalIp();
		};
		const onOffline = () => {
			dispatch(connectionQuality.actions.setOnline(false));
		};
		window.addEventListener("online", onOnline);
		window.addEventListener("offline", onOffline);
		return () => {
			window.removeEventListener("online", onOnline);
			window.removeEventListener("offline", onOffline);
		};
	}, [dispatch, refreshExternalIp]);

	useEffect(() => {
		refreshExternalIp();
	}, [refreshExternalIp]);

	// A new PRPC session starts a fresh window; false -> true after the
	// first connection is a reconnect.
	const wasConnectedRef = useRef(false);
	useEffect(() => {
		dispatch(connectionQuality.actions.resetSamples());
		if (!connected) return;
		if (wasConnectedRef.current) {
			dispatch(connectionQuality.actions.addReconnect());
		}
		wasConnectedRef.current = true;
	}, [connected, dispatch]);

	useEffect(() => {
		if (!connected) return undefined;

		let cancelled = false;
		let inFlight = false;

		const tick = async () => {
			if (inFlight || document.hidden) return;
			inFlight = true;
			const sample = await pingServer();
			inFlight = false;
			if (cancelled) return;
			dispatch(
				connectionQuality.actions.addSample({ sample, at: Date.now() }),
			);
		};

		const onVisibility = () => {
			if (!document.hidden) tick();
		};

		tick();
		const interval = setInterval(tick, PING_INTERVAL_MS);
		document.addEventListener("visibilitychange", onVisibility);
		return () => {
			cancelled = true;
			clearInterval(interval);
			document.removeEventListener("visibilitychange", onVisibility);
		};
	}, [connected, dispatch]);

	const { level, lost } = getConnectionStats({ online, connected, samples });
	const lastSampleLost =
		samples.length > 0 && samples[samples.length - 1] === null;

	// Look outside only when our link looks bad, so a healthy dispatcher
	// sends nothing to Google or Cloudflare.
	const lastExternalRef = useRef(0);
	useEffect(() => {
		if (!online) return;
		if (!needsExternalCheck(level) && lost === 0) return;
		if (Date.now() - lastExternalRef.current < EXTERNAL_CHECK_MIN_GAP_MS) {
			return;
		}
		lastExternalRef.current = Date.now();

		(async () => {
			const [googleOk, cloudflare] = await Promise.all([
				checkGoogle(),
				readCloudflare(),
			]);
			if (cloudflare.ip) {
				dispatch(
					connectionQuality.actions.setExternalIp(cloudflare.ip),
				);
			}
			dispatch(
				connectionQuality.actions.setExternal({
					verdict: getInternetVerdict({
						googleOk,
						cloudflareOk: cloudflare.ok,
						serverReachable: connected && !lastSampleLost,
					}),
					checkedAt: Date.now(),
				}),
			);
		})();
	}, [connected, dispatch, lastCheckAt, lastSampleLost, level, lost, online]);
};

export default useConnectionQualityMonitor;
